import { WorkflowEditorService } from 'api-clients/monolith';
import { useEffect, useState } from 'react';

export interface RehireMetadataField {
  key: string;
  remoteKey: string | null;
}

export type RehireMetadataErrorReason =
  | 'no_connector'
  | 'connector_ambiguous'
  | 'filter_unsupported_suspected'
  | 'identifier_mismatch'
  | 'multiple_distinct_identities'
  | 'setup_sync_pending'
  | 'qa_failed'
  | 'connector_invalid'
  | 'permission_missing'
  | 'kombo_rate_limited'
  | 'kombo_error';

export interface RehireMetadata {
  fields: RehireMetadataField[];
  observedValues: Array<{ value: string; count: number }>;
  sampleSize: number;
  syncState: {
    initialSyncCompleted: boolean;
    lastSyncedAt: string | null;
    error?: { reason: RehireMetadataErrorReason; code: string | null } | null;
  };
}

const EMPTY_METADATA: RehireMetadata = {
  fields: [],
  observedValues: [],
  sampleSize: 0,
  syncState: { initialSyncCompleted: false, lastSyncedAt: null, error: null },
};

const FAILED_METADATA: RehireMetadata = {
  ...EMPTY_METADATA,
  syncState: {
    ...EMPTY_METADATA.syncState,
    error: { reason: 'kombo_error', code: null },
  },
};

async function fetchMetadata(
  connectorUuid: string,
  eligibilityField?: string,
): Promise<RehireMetadata> {
  const response = eligibilityField
    ? await WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckMetadata(
        connectorUuid,
        eligibilityField,
      )
    : await WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckMetadata(
        connectorUuid,
      );
  return {
    ...response.metadata,
    syncState: {
      ...response.metadata.syncState,
      error: response.metadata.syncState.error ?? null,
    },
  };
}

export function useRehireMetadata(
  connectorUuid: string,
  eligibilityField: string,
): { metadata: RehireMetadata; loading: boolean; refetch: () => void } {
  const [metadata, setMetadata] = useState<RehireMetadata>(EMPTY_METADATA);
  const [loading, setLoading] = useState(Boolean(connectorUuid));
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    void requestVersion;
    let mounted = true;
    if (!connectorUuid) {
      setMetadata(EMPTY_METADATA);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    void fetchMetadata(connectorUuid)
      .then(async baseMetadata => {
        const field =
          eligibilityField ||
          (baseMetadata.fields.length === 1 ? baseMetadata.fields[0].key : '');
        return field && !baseMetadata.syncState.error
          ? fetchMetadata(connectorUuid, field)
          : baseMetadata;
      })
      .then(result => {
        if (mounted) setMetadata(result);
      })
      .catch(() => {
        if (mounted) setMetadata(FAILED_METADATA);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [connectorUuid, eligibilityField, requestVersion]);

  return {
    metadata,
    loading,
    refetch: () => setRequestVersion(version => version + 1),
  };
}
